import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useBtcCorrelation } from "@/hooks/useBtcCorrelation";
import { Link2, Link2Off, Bitcoin } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  symbol: string;
  /** compact = icon + percent only, default = icon + label + percent */
  variant?: "compact" | "default";
  className?: string;
}

/**
 * BTC Correlation / Beta badge.
 * - >= 0.70  → "BTC-tied" (red): moves with BTC, no diversification
 * - 0.40–0.69 → "BTC-aware" (amber): partial coupling
 * - < 0.40  → "Decoupled" (green): independent of BTC
 *
 * Data is sourced from the cached_advanced_metrics table (recomputed
 * by the calculate-advanced-metrics edge function), so no extra API calls.
 */
export const BtcCorrelationBadge = ({ symbol, variant = "default", className }: Props) => {
  const { metric, isLoading } = useBtcCorrelation(symbol);

  if (isLoading || !metric) return null;

  const c = metric.btc_correlation_30d; // 0..1
  const pct = Math.round(c * 100);

  let label = "BTC-aware";
  let tone = "text-amber-400 border-amber-400/40 bg-amber-400/10";
  let Icon = Bitcoin;
  let helper = "Partially follows BTC. Some independent moves expected.";

  if (c >= 0.7) {
    label = "BTC-tied";
    tone = "text-destructive border-destructive/40 bg-destructive/10";
    Icon = Link2;
    helper = "Moves closely with BTC — limited diversification benefit.";
  } else if (c < 0.4) {
    label = "Decoupled";
    tone = "text-success border-success/40 bg-success/10";
    Icon = Link2Off;
    helper = "Trades independently of BTC — useful diversifier when BTC corrects.";
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap",
              tone,
              className,
            )}
          >
            <Icon className="h-3 w-3" />
            {variant === "default" && <span>{label}</span>}
            <span className="font-mono">{pct}%</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-xs">
          <div className="font-semibold mb-1">BTC correlation: {pct}%</div>
          <div className="text-muted-foreground">{helper}</div>
          <div className="mt-1 text-[10px] text-muted-foreground/80">
            30-day signal · 7d vs BTC: {metric.btc_relative_strength_7d > 0 ? "+" : ""}
            {metric.btc_relative_strength_7d.toFixed(1)}%
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
